import { Video, User, Clock } from 'lucide-react';
import type { AppointmentListItem, AppointmentStatus } from '@/api';
import { cn } from '@/utils/cn';

export interface AppointmentGridCardProps {
  appointment: AppointmentListItem;
  startHour?: number;
  hourHeight?: number;
  onClick: (appointment: AppointmentListItem) => void;
}

export function AppointmentGridCard({
  appointment,
  startHour = 7,
  hourHeight = 72,
  onClick,
}: AppointmentGridCardProps) {
  const startDate = new Date(appointment.scheduledAt);
  const startHours = startDate.getHours();
  const startMinutes = startDate.getMinutes();
  const totalMinutesFromMidnight = startHours * 60 + startMinutes;
  const startLimitMinutes = startHour * 60;

  // Offset vertical em pixels a partir do início da grade
  const top = Math.max(0, ((totalMinutesFromMidnight - startLimitMinutes) / 60) * hourHeight);
  const duration = appointment.duration || 30;
  // Subtrai 2px para criar uma respiração visual entre slots contíguos
  const height = Math.max((duration / 60) * hourHeight - 2, 28);

  // Calcula o horário de término para exibição
  const endDate = new Date(startDate.getTime() + duration * 60 * 1000);
  const startTimeStr = `${String(startHours).padStart(2, '0')}:${String(startMinutes).padStart(2, '0')}`;
  const endTimeStr = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;

  const patientName = appointment.patient?.name || 'Paciente sem nome';
  const isTelemedicine = Boolean(appointment.joinUrl || appointment.canJoin || appointment.type === 'EMERGENCY');

  // Estilos por status
  const getStatusStyles = (status: AppointmentStatus) => {
    switch (status) {
      case 'PAID':
      case 'DOCTOR_ACCEPTED':
        return {
          container:
            'bg-emerald-50 text-emerald-900 border-emerald-300 hover:border-emerald-500 hover:bg-emerald-100/70 dark:bg-emerald-950/40 dark:text-emerald-100 dark:border-emerald-700/60 shadow-xs',
          badge: 'bg-emerald-500/20 text-emerald-800 dark:text-emerald-300',
          label: status === 'PAID' ? 'Pago' : 'Aceita',
          dot: 'bg-emerald-500',
        };
      case 'IN_PROGRESS':
        return {
          container:
            'bg-primary/10 text-primary border-primary/50 ring-2 ring-primary/40 ring-offset-1 animate-pulse dark:bg-primary/20 dark:text-primary-foreground shadow-md',
          badge: 'bg-primary text-primary-foreground font-semibold',
          label: 'Em Andamento',
          dot: 'bg-primary animate-ping',
        };
      case 'WAITING_PAYMENT':
        return {
          container:
            'bg-amber-50 text-amber-900 border-amber-300 hover:border-amber-500 hover:bg-amber-100/70 dark:bg-amber-950/40 dark:text-amber-100 dark:border-amber-700/60 shadow-xs',
          badge: 'bg-amber-500/20 text-amber-800 dark:text-amber-300',
          label: 'Aguardando Pgto',
          dot: 'bg-amber-500',
        };
      case 'CANCELLED':
        return {
          container:
            'bg-muted/40 text-muted-foreground border-border/80 opacity-60 line-through hover:opacity-80 dark:bg-muted/20',
          badge: 'bg-muted text-muted-foreground',
          label: 'Cancelada',
          dot: 'bg-muted-foreground',
        };
      case 'FINISHED':
      case 'COMPLETED':
        return {
          container:
            'bg-slate-50 text-slate-800 border-slate-200 hover:bg-slate-100 dark:bg-slate-900/40 dark:text-slate-200 dark:border-slate-800',
          badge: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
          label: 'Concluída',
          dot: 'bg-slate-400',
        };
      case 'WAITING_DOCTOR':
      case 'CREATED':
      default:
        return {
          container:
            'bg-sky-50 text-sky-900 border-sky-300 hover:border-sky-500 hover:bg-sky-100/70 dark:bg-sky-950/40 dark:text-sky-100 dark:border-sky-700/60 shadow-xs',
          badge: 'bg-sky-500/20 text-sky-800 dark:text-sky-300',
          label: status === 'WAITING_DOCTOR' ? 'Aguardando' : 'Agendada',
          dot: 'bg-sky-500',
        };
    }
  };

  const statusInfo = getStatusStyles(appointment.status);
  const isCompact = height < 44;

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick(appointment);
      }}
      style={{
        top: `${top}px`,
        height: `${height}px`,
      }}
      className={cn(
        'absolute left-1 right-1 rounded-md border p-1.5 text-left transition-all overflow-hidden cursor-pointer z-10',
        'hover:z-20 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/50',
        statusInfo.container
      )}
      title={`${patientName} (${startTimeStr} - ${endTimeStr}) - ${statusInfo.label}`}
    >
      <div className="flex h-full flex-col justify-between overflow-hidden">
        {/* Linha superior: Horário + Tipo */}
        <div className="flex items-center justify-between gap-1 text-[11px] leading-none">
          <div className="flex items-center gap-1 font-semibold">
            <Clock className="h-3 w-3 shrink-0 opacity-70" />
            <span>{startTimeStr} - {endTimeStr}</span>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {isTelemedicine ? (
              <span title="Telemedicina" className="p-0.5 rounded bg-background/50">
                <Video className="h-3 w-3 text-primary" />
              </span>
            ) : (
              <span title="Presencial" className="p-0.5 rounded bg-background/50">
                <User className="h-3 w-3 text-muted-foreground" />
              </span>
            )}
          </div>
        </div>

        {/* Linha do Paciente */}
        {!isCompact ? (
          <div className="flex items-center justify-between gap-1 mt-0.5 min-w-0">
            <span className="font-semibold text-xs truncate max-w-full">
              {patientName}
            </span>
            <span
              className={cn(
                'text-[9px] font-medium px-1.5 py-0.5 rounded-full shrink-0 tracking-tight',
                statusInfo.badge
              )}
            >
              {statusInfo.label}
            </span>
          </div>
        ) : (
          <div className="truncate text-[10px] font-medium opacity-90">
            {patientName}
          </div>
        )}
      </div>
    </button>
  );
}
